import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

export type DeliveryMethod = 'STANDARD' | 'EXPRESS' | 'NEXTDAY' | 'COLLECTION';
export type RefundMethod = 'PAYPAL' | 'TEYA';
export type PaymentMethod = 'TEYA' | 'PAYPAL' | 'BANK_TRANSFER' | 'INVOICE';

export interface SiteSettings {
  delivery: Record<DeliveryMethod, { website: boolean; backOffice: boolean }>;
  // Older backends don't send it - treat every method as on.
  payments?: Record<PaymentMethod, { website: boolean; backOffice: boolean }>;
  refunds: Record<RefundMethod, boolean>;
  updatedBy?: string;
  updatedAt?: string;
}

// Used if the settings can't be loaded: today's behaviour, so a hiccup never
// hides an option staff rely on.
const FALLBACK: SiteSettings = {
  delivery: {
    STANDARD: { website: true, backOffice: true },
    EXPRESS: { website: false, backOffice: true },
    NEXTDAY: { website: false, backOffice: true },
    COLLECTION: { website: true, backOffice: true },
  },
  refunds: { PAYPAL: true, TEYA: true },
};

@Injectable({ providedIn: 'root' })
export class SiteSettingsService {
  private settings$?: Observable<SiteSettings>;

  constructor(private http: HttpClient) {}

  get(): Observable<SiteSettings> {
    if (!this.settings$) {
      this.settings$ = this.http.get<SiteSettings>('/site-settings').pipe(
        catchError(() => of(FALLBACK)),
        shareReplay(1),
      );
    }
    return this.settings$;
  }

  // Owner-only: includes who last changed it and when.
  getForOwner(): Observable<SiteSettings> {
    return this.http.get<SiteSettings>('/site-settings/admin');
  }

  save(settings: SiteSettings): Observable<SiteSettings> {
    return this.http.put<SiteSettings>('/site-settings', settings).pipe(
      tap(() => (this.settings$ = undefined)),
    );
  }

  isDeliveryEnabledInBackOffice(settings: SiteSettings, method: string): boolean {
    const entry = settings.delivery[method as DeliveryMethod];
    return entry ? entry.backOffice : true;
  }

  isPaymentEnabledInBackOffice(settings: SiteSettings | null | undefined, method: string): boolean {
    const entry = settings?.payments?.[method as PaymentMethod];
    return entry ? entry.backOffice : true;
  }

  isRefundEnabled(settings: SiteSettings, paymentMethod: string): boolean {
    const enabled = settings.refunds[paymentMethod as RefundMethod];
    return enabled === undefined ? true : enabled;
  }
}
